import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { createBaseChartConfig, type ChartData } from './chartConfigUtils';

interface DailyPaidContractsCountData {
    date: string;
    count: number;
}

interface DailyPaidContractsCountChartProps {
    data: DailyPaidContractsCountData[];
    loading?: boolean;
    referenceValue?: number;
}

const DailyPaidContractsCountChart: React.FC<DailyPaidContractsCountChartProps> = ({
    data,
    loading = false,
    referenceValue,
}) => {
    const { t } = useTranslation();

    const chartData: ChartData[] = data.map(d => ({ date: d.date, value: d.count ?? 0 }));
    const options: Highcharts.Options = {
        ...createBaseChartConfig(
            chartData,
            {
                chartType: 'column',
                yAxisTitle: t(
                    'statistics.charts.numberOfPaidContracts',
                    'Number of Paid Contracts',
                ),
                yAxisColor: '#fa8c16',
                seriesName: t('statistics.charts.paidContractsCount', 'Paid Contracts Count'),
                seriesColor: '#fa8c16',
                referenceValue,
            },
            t,
        ),
        plotOptions: {
            column: {
                borderRadius: 2,
                animation: !loading,
            },
        },
        tooltip: {
            shared: true,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderColor: '#ccc',
            borderRadius: 3,
            shadow: true,
        },
    };

    return (
        <div>
            {loading && (
                <div
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1000,
                        color: '#fa8c16',
                    }}
                >
                    {t('statistics.charts.loading', 'Loading chart...')}
                </div>
            )}
            {data.length === 0 && !loading && (
                <div
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1000,
                        color: '#888',
                        fontSize: '12px',
                    }}
                >
                    {t('statistics.charts.noData', 'No data available')}
                </div>
            )}
            <HighchartsReact
                highcharts={Highcharts}
                options={options}
                allowChartUpdate={true}
                immutable={false}
                updateArgs={[true, true, true]}
            />
        </div>
    );
};

export default DailyPaidContractsCountChart;
